import React, { useEffect } from 'react';
import { Card, Typography, Row, Col, Button } from 'antd';
import { useHistory } from 'react-router-dom';
import { ShoppingCartOutlined, HeartOutlined, HomeOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const NewYork = () => {
  const history = useHistory();

  // Add structured data for local SEO
  useEffect(() => {
    const localSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Comfort Bears in New York - Lumi Bears",
      "description": "Premium comfort companions for New York families. Emotional support bears designed to help children in NYC with bedtime routines and emotional regulation.",
      "url": "https://lumibears.com/locations/new-york",
      "about": {
        "@type": "Product",
        "name": "Lumi Comfort Bears",
        "description": "Emotional support companions for children"
      },
      "audience": {
        "@type": "Audience", 
        "geographicArea": {
          "@type": "City",
          "name": "New York",
          "containedInPlace": {
            "@type": "State", 
            "name": "New York"
          }
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(localSchema);
    document.head.appendChild(script);

    // Update page title and meta description
    document.title = "Comfort Bears New York - Emotional Support for NYC Children | Lumi Bears";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Premium comfort bears for New York families. Help NYC children with bedtime anxiety and emotional support. Fast shipping to Manhattan, Brooklyn, Queens.');
    }

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent.includes('new-york')) {
        existingScript.remove();
      }
      // Reset title and description  
      document.title = "Lumi Bears - Comfort Companions for Sensitive Children | Emotional Support Teddy Bears";
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Premium comfort companions for children who need extra emotional support. Soft, intelligent teddy bears designed to help kids feel calm and secure during bedtime and challenging moments.');
      }
    };
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Hero Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '60px 0',
        color: 'white'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Row gutter={[48, 32]} align="middle">
            <Col xs={24} lg={14}>
              <Title level={1} style={{ color: 'white', marginBottom: '20px', fontSize: '2.5rem' }}>
                Comfort Bears in New York
              </Title>
              <Paragraph style={{ fontSize: '18px', color: 'rgba(255,255,255,0.9)', marginBottom: '30px' }}>
                Supporting NYC families with premium comfort companions designed for children who need 
                extra emotional support. From Manhattan to Brooklyn, Queens to the Bronx - we're here 
                to help your child feel calm and secure.
              </Paragraph>
              <Button 
                type="primary" 
                size="large" 
                icon={<ShoppingCartOutlined />}
                onClick={() => history.push('/')}
                style={{ 
                  background: 'white', 
                  borderColor: 'white', 
                  color: '#667eea',
                  height: '50px',
                  fontSize: '16px'
                }}
              >
                Shop Comfort Bears
              </Button>
            </Col>
            <Col xs={24} lg={10}>
              <div style={{ textAlign: 'center' }}>
                <HomeOutlined style={{ fontSize: '120px', color: 'rgba(255,255,255,0.3)' }} />
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '60px 20px' }}>
        
        {/* Why NYC Families Choose Lumi Bears */}
        <Row gutter={[48, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} lg={16}>
            <Title level={2} style={{ color: '#333', marginBottom: '24px' }}>
              Why New York Families Trust Lumi Bears
            </Title>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.7', color: '#666', marginBottom: '20px' }}>
              Life in New York City brings unique challenges for families. From busy schedules to small living spaces, 
              NYC parents need comfort solutions that work in real urban family life. Lumi Bears are designed to provide 
              consistent emotional support that fits seamlessly into your family's routine.
            </Paragraph>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.7', color: '#666' }}>
              Whether you're in a Manhattan apartment, a Brooklyn brownstone, or a Queens family home, our comfort bears 
              adapt to your space while providing the same reliable emotional support your child needs to thrive in 
              the city that never sleeps.
            </Paragraph>
          </Col>
          <Col xs={24} lg={8}>
            <Card style={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <HeartOutlined style={{ fontSize: '48px', color: '#667eea', marginBottom: '16px' }} />
              <Title level={4} style={{ color: '#333' }}>Designed for City Life</Title>
              <Text style={{ color: '#666' }}>
                Compact, durable, and perfect for urban families who need reliable comfort solutions.
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Shipping & Delivery Info */}
        <Card style={{ marginBottom: '40px', background: '#f8f9fa' }}>
          <Row gutter={[32, 24]} align="middle">
            <Col xs={24} md={4} style={{ textAlign: 'center' }}>
              <ClockCircleOutlined style={{ fontSize: '48px', color: '#667eea' }} />
            </Col>
            <Col xs={24} md={20}>
              <Title level={3} style={{ color: '#333', marginBottom: '16px' }}>
                Fast Delivery to New York Area
              </Title>
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>Manhattan:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>Brooklyn & Queens:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>Bronx & Staten Island:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
              </Row>
              <Paragraph style={{ marginTop: '16px', color: '#666', marginBottom: 0 }}>
                <Text italic>
                  All orders ship with tracking. Current delivery time reflects our pre-order production schedule 
                  as we carefully craft each comfort companion.
                </Text>
              </Paragraph>
            </Col>
          </Row>
        </Card>

        {/* NYC-Specific Challenges */}
        <Title level={2} style={{ color: '#333', marginBottom: '30px', textAlign: 'center' }}>
          Supporting NYC Children Through Daily Challenges
        </Title>
        
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Small Space Living</Title>
              <Paragraph style={{ color: '#666' }}>
                NYC apartments require comfort solutions that don't take up precious space. [Lumi Bears](/products) provide 
                maximum emotional support in a compact, portable design that works in any living situation. Our [travel-sized companions](/products/lumi-travel) are especially popular with Manhattan families.
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Busy Family Schedules</Title>
              <Paragraph style={{ color: '#666' }}>
                Between work, school, and city activities, NYC families are always on the go. [Comfort bears](/products) 
                provide consistent emotional support that travels with your child, from home to school to weekend activities. For busy commutes, try our [pocket-sized comfort companions](/products/lumi-pocket).
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Urban Overstimulation</Title>
              <Paragraph style={{ color: '#666' }}>
                City life can be overwhelming for sensitive children. Our comfort bears offer a calming presence 
                that helps kids self-regulate in stimulating urban environments.
              </Paragraph>
            </Card>
          </Col>
        </Row>

        {/* Family Resources for NYC */}
        <Card style={{ marginBottom: '40px' }}>
          <Title level={3} style={{ color: '#333', marginBottom: '24px' }}>
            Resources for New York Families
          </Title>
          <Row gutter={[32, 24]}>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#4a90e2' }}>Child Development Support</Title>
              <ul style={{ color: '#666', lineHeight: '1.6' }}>
                <li>
                  <a href="https://www.nyc.gov/site/doh/health/health-topics/child-development.page" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>
                    NYC Department of Health - Child Development Resources
                  </a>
                </li>
                <li>
                  <a href="https://www.schools.nyc.gov/school-life/special-situations/students-with-disabilities" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>
                    NYC Department of Education - Student Support Services
                  </a>
                </li>
              </ul>
            </Col>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#4a90e2' }}>Family Wellness Resources</Title>
              <ul style={{ color: '#666', lineHeight: '1.6' }}>
                <li>
                  <a href="https://www.nycgovparks.org/programs/recreation-centers" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>
                    NYC Parks - Family Recreation Programs
                  </a>
                </li>
                <li>
                  <a href="https://www.nypl.org/locations/map" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>
                    New York Public Library - Children's Programs
                  </a>
                </li>
              </ul>
            </Col>
          </Row>
        </Card>

        {/* Call to Action */}
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
          borderRadius: '12px'
        }}>
          <Title level={2} style={{ color: '#4a148c', marginBottom: '20px' }}>
            Ready to Support Your NYC Child?
          </Title>
          <Paragraph style={{ fontSize: '18px', color: '#6a1b9a', marginBottom: '30px', maxWidth: '600px', margin: '0 auto 30px auto' }}>
            Join New York families who are giving their children the emotional support tools they need to thrive in the city.
          </Paragraph>
          <Button 
            type="primary" 
            size="large" 
            icon={<ShoppingCartOutlined />}
            onClick={() => history.push('/')}
            style={{ 
              background: '#4a148c', 
              borderColor: '#4a148c',
              height: '50px',
              fontSize: '18px',
              padding: '0 30px'
            }}
          >
            Shop Comfort Bears
          </Button>
        </div>

      </div>
    </div>
  );
};

export default NewYork;